//
// Given 'stats' and 'games', generate NetStats for each player in each position.
//
import { addDoc, arrayRemove, arrayUnion, collection, deleteDoc, deleteField, doc, getDocs, updateDoc, writeBatch } from 'firebase/firestore'
import { CallbackInterface, useRecoilCallback } from 'recoil'

import {
  ALL_PREFS,
  LOCAL_STORAGE_KEY_SETTINGS,
  MIN_NUM_QTRS_PER_COMBO,
  NORMALIZE_MIN_QTRS,
  NULL_NETSTAT_VALUE,
  PREFS_PREFER,
  PRESET_CUSTOM,
  dbids,
  defaultPreset,
  presets,
  queryFieldPositions,
} from './definitions'
import { db } from './firebase'
import { fetchJson, loadFromLocalStorage, parseData } from './util'
import { forEachPairContribution, forEachTrioContribution } from './rosterAnalysis'
import {
	FixtureSyncGame,
	buildFixtureSyncResult,
	canUseHeadlineFinalScore,
	makeFinalOnlyResult,
	pairFixtureGames,
	rememberPeriodScoreFetchAttempt,
	shouldFetchPeriodScores,
} from './fixtureSync'
import {
	PlayHqFixtureGame,
	fetchPlayHqFixtureGames,
	isCompletedPlayHqStatus,
} from './playHqApi'
import { fetchPlayHqResult } from './playHqScoreSync'
import { formatResult, hasDetailedPeriodScores, isValidResult } from './resultValidation'
import {
	myGames,
	myTeam,
  newSeasonConnector,
  newTeamId,
  section,
  queryDbid,
} from './data'

export { formatResult, validateResult } from './resultValidation'

type Items = {
	games: Games | undefined
	players: Players | undefined
	rosters: Rosters
	numPeriods: NumPeriods
	gameFormat: GameFormat
}

export function generateAllNetStats(items: Items, data?: AllPlayerStats): AllNetStats {

	// console.log('generateAllNetStats()', items, data)

	const { games, players, numPeriods, gameFormat } = items

	const fieldPositions = queryFieldPositions(gameFormat)

	const allNetStats: AllNetStats = { }

	if (games && players) {

		const qtrGoalDiffs = calculateQtrGoalDiffs({
			games,
			numPeriods,
		})

		// console.log('qtr goal diffs', qtrGoalDiffs)

		if (Object.keys(qtrGoalDiffs).length) {

			const stats = data || parseData(items)
			// console.log('My parseData result is', stats)

			Object.keys(players).forEach(playerId => {

				const netStats = new Array(fieldPositions.length + 1).fill(null)
				const posCount = new Array(fieldPositions.length + 1).fill(0)

				const { games: myGames } = stats[playerId]

				// console.log(playerId, 'played', myGames)

				Object.keys(qtrGoalDiffs).forEach(gameId => {
					if (myGames[gameId]) {
						const myPositions = myGames[gameId]
						myPositions.forEach((posArr, qtr) => {
							posArr.forEach(pos => {
								const posIndex = pos ?? fieldPositions.length
								const gameTime = 1 / posArr.length 	// Usually 1, but 0.5 for subs

								const diff = qtrGoalDiffs[gameId][qtr]

								if (diff !== null) {
									const currentNetStat = netStats[posIndex] || 0
									netStats[posIndex] = currentNetStat + (diff * gameTime)
								}

								posCount[posIndex] += gameTime
								// console.log(playerId, "game", gameId, "qtr", qtr, "posIndex", posIndex, "goaldiff", diff, '*', gameTime, '=', (diff || 0) * gameTime)
							})
						})
					}
				})

				//
				// For gameFormats where there are duplicate positions (e.g. 2 x "A", 2 x "D"),
				// we merge the posCount and netStats for those positions.
				//
				fieldPositions.forEach((pos, posIndex) => {
					if (pos === fieldPositions[posIndex + 1]) {
						posCount[posIndex + 1] += posCount[posIndex]
						netStats[posIndex + 1] += netStats[posIndex]
						posCount[posIndex] = 0
						netStats[posIndex] = null
					}
				})

				allNetStats[playerId] = {
					raw: netStats,
					posCount,
					normalized: [ ],
					adjusted: [ ],
				}

				allNetStats[playerId].normalized = netStats.map((v, index) => {
					let newValue = v
					if (v !== null) {
						newValue /= (posCount[index] || 1)
						if (posCount[index] < NORMALIZE_MIN_QTRS) {
							//
							// We scale netStat scores way down when there's a very small sample size.
							//
							newValue *= 0.80 / Math.pow(NORMALIZE_MIN_QTRS - posCount[index], 1.5)
						}
					}
					return newValue
				})

				//
				// 'adjusted' is what generateRoster uses -- players who have never played a position have
				// a value of NULL_NETSTAT_VALUE (e.g. -1.25), and players with very few quarters share
				// some of that penalty.
				//
				// Because the thing is you don't want to get carried away with a player who has decent stats from 2 qtrs.
				// If she's -0.3 that's not great but a lot better than NULL_NETSTAT_VALUE.
				//
				const allNetStatAbsValues = [ ...(Object.values(allNetStats).map(obj => obj.normalized.map(n => Math.abs(n))).flat()) ]
				const max = Math.max(...allNetStatAbsValues)
				const scale = 1.50 / (max || 1)
					// console.log('allNetStatAbsValues', max, scale, allNetStatAbsValues)

					allNetStats[playerId].adjusted = allNetStats[playerId].normalized.map((v, index) => {
						let newValue = v
						if (v === null) {
							newValue = NULL_NETSTAT_VALUE
						} else {
							newValue *= scale
							if (posCount[index] * 1.5 < NORMALIZE_MIN_QTRS) {
								//
								// We should try to avoid rostering players in roles they've never played before, but we also
								// don't want a player to gain +NULL_NETSTAT_VALUE just from playing a single qtr. So let's
								// remove the NULL_NETSTAT_VALUE at 1.5X, so that you no longer have any penalty after
								// playing 4 qtrs (assuming NORMALIZE_MIN_QTRS == 6).
								//
								const adj = NULL_NETSTAT_VALUE * ((NORMALIZE_MIN_QTRS - 1.5 * posCount[index]) / NORMALIZE_MIN_QTRS)
								newValue += adj
							}
						}
						return newValue
					})

				// console.log(playerId, 'final netstats', allNetStats[playerId], 'from raw stats', netStats, 'with posCount', posCount)
			})
		}

	}

	// console.log('allNetStats', allNetStats)

	return allNetStats
}

type calculateQtrGoalDiffsProps = {
	games: Games
	numPeriods: number
	raw?: boolean
}

//
// By default, returns normalized qtr goal diffs.
//
export function calculateQtrGoalDiffs(props: calculateQtrGoalDiffsProps) {

	const { games, raw, numPeriods } = props

	const qtrGoalDiffs: {
		[key: GameId]: Array<number|null>
	} = { }

    if (games) {

		const qtrs = [ ...Array(numPeriods).keys() ]

        Object.keys(games).forEach(gameId => {

			//
			// Ensure that we use validated 'results', so we don't get
			// confused if the user enters scores, then changes the season's
			// numPeriods.
			//
			const result = formatResult(games[gameId].result, numPeriods)

            //
            // True if at least 1 qtr (other than the last) has non-null scores.
            //
            // This may mean that only some do.
            //
            const haveQtrByQtrScores = qtrs.some(qtr => qtr < (numPeriods - 1) && result.me[qtr] !== null && result.me[qtr] !== undefined && result.them[qtr] !== null && result.them[qtr] !== undefined)

			// console.log('goal diffs?', haveQtrByQtrScores, 'with numPeriods', numPeriods, 'for result', result)

            if (haveQtrByQtrScores) {

                const qtrGoalsMe = result.me.map((goals, qtr) => {
					const g = goals ?? 0
                    if (!qtr)
                        return g
					return g - (result.me[qtr - 1] || 0)
                })

                const qtrGoalsThem = result.them.map((goals, qtr) => {
					const g = goals ?? 0
                    if (!qtr)
                        return g
					return g - (result.them[qtr - 1] || 0)
                })

                qtrGoalDiffs[gameId] = qtrs.map(qtr => {

                    let diff = qtrGoalsMe[qtr] - qtrGoalsThem[qtr]

                    // Normalize!
                    if (!raw) {
						const finalPeriod = numPeriods - 1
                        const avgDiffPerQtr = ((result.me[finalPeriod] || 0) - (result.them[finalPeriod] || 0)) / numPeriods
						diff -= avgDiffPerQtr
					}

                    return diff
                })
            }
        })
    }

    // console.log('qtrGoalDiffs', qtrGoalDiffs)

    return qtrGoalDiffs
}

export function calculateRosterNetStats(data: AllRosterStats, numPeriods: number): RosterNetStats {

	// console.log('calculateRosterNetStats', data)

	const qtrs = new Array(numPeriods).fill(0)

	Object.keys(data).forEach(player => {
		data[player].forEach((obj, qtr) => {
			if (obj) {
				const { netStat, netStatContribution, pair, pairContribution, trio, trioContribution, playing } = obj
				if (playing) {

					if (netStatContribution ?? netStat) {
						qtrs[qtr] += netStatContribution ?? netStat ?? 0
					}

					if (pairContribution ?? pair) {
						qtrs[qtr] += pairContribution ?? pair ?? 0
					}

					if (trioContribution ?? trio) {
						qtrs[qtr] += trioContribution ?? trio ?? 0
					}
				}
			}
		})
	})

	return {
		qtrs,
		total: qtrs.reduce((total, score) => total + score, 0),
	}
}

type findPairingsProps = {
	games: Games
	onCourtRosters: OnCourtRosters
	numPeriods: NumPeriods
	gameFormat: GameFormat
	fillins?: Players | null
	findTrios?: boolean
	userHasPro?: boolean
}

export function findPairings(props: findPairingsProps): Array<Pair> {

    const { games, fillins, onCourtRosters, numPeriods, gameFormat, findTrios, userHasPro } = props

    const qtrGoalDiffs = calculateQtrGoalDiffs({
		games,
		numPeriods,
	})

	    // console.log('findPairings()', { ...props, qtrGoalDiffs })

	const allPairings: {
		[key: string]: {
			diff: number
			n: number
			games: Array<GamePair>
			numPeriods: number
		}
	} = { }

	    Object.keys(qtrGoalDiffs).forEach(gameId => {
	        qtrGoalDiffs[gameId].forEach((diff, qtr) => {

	            if (diff === null) {
	                return
	            }

				if (!onCourtRosters[gameId]) {
					return
				}

				const addPairing = (pairId: string, amount: number) => {
					const game: GamePair = {
						...games[gameId],
						qtr,
						diff: diff * amount,
					}

					if (!allPairings[pairId]) {
						allPairings[pairId] = {
							diff: diff * amount,
							n: amount,
							games: [ game ],
							numPeriods,
						}
					} else {
						allPairings[pairId].n += amount
						allPairings[pairId].diff += diff * amount
						const existingGame = allPairings[pairId].games.find(g => g.id === game.id && g.qtr === game.qtr)
						if (existingGame) {
							existingGame.diff += game.diff
						} else {
							allPairings[pairId].games.push(game)
						}
					}
				}

				if (!findTrios) {
					forEachPairContribution({
						onCourtRoster: onCourtRosters[gameId],
						numPeriods,
						gameFormat,
						qtr,
					}, ({ playerId1, posIndex1, playerId2, posIndex2, amount }) => {
						if (fillins?.[playerId1] || fillins?.[playerId2]) {
							return
						}

						addPairing(`${playerId1}__${posIndex1}__${playerId2}__${posIndex2}`, amount)
					})
				} else {
					forEachTrioContribution({
						onCourtRoster: onCourtRosters[gameId],
						numPeriods,
						gameFormat,
						qtr,
					}, ({ playerId1, posIndex1, playerId2, posIndex2, playerId3, posIndex3, amount }) => {
						if (fillins?.[playerId1] || fillins?.[playerId2] || fillins?.[playerId3]) {
							return
						}

						addPairing(`${playerId1}__${posIndex1}__${playerId2}__${posIndex2}__${playerId3}__${posIndex3}`, amount)
					})
				}
			})
		})

	const sortedPairings: Array<Pair> = Object.keys(allPairings).sort((a, b) => allPairings[b].diff - allPairings[a].diff).filter(id => allPairings[id].n >= MIN_NUM_QTRS_PER_COMBO).map((id, i) => {
		const { n, diff, games } = allPairings[id]
		const [ playerId1, positionIndex1, playerId2, positionIndex2, playerId3, positionIndex3 ] = id.split('__')
		return {
			id,
			i,
			n,
			diff,
			games,
			avg: diff / n,
			playerId1,
			positionIndex1: Number(positionIndex1),
			playerId2,
			positionIndex2: Number(positionIndex2),
			playerId3,
			positionIndex3: Number(positionIndex3),
			numPeriods,
		}
	})

	//
	// For non-Pro users, only show the first (best) pair/trio.
	//
	if (!userHasPro) {
		return sortedPairings.map((obj, index) => {
			if (!index) {
				return obj
			}
			return {
				...obj,
				dummy: true,
				games: [ ],
			}
		})
	}

	// console.log('findPairings() result', sortedPairings)

	return sortedPairings
}

//
//
// Load any saved 'settings' & validate, or else set to default
// values.
//
type DefaultKeys = keyof typeof presets[typeof defaultPreset]['defaults']

export function initializeSettings() {
    //
    // First build default values. We might add new settings in the future,
    // so do this even if we're about to overwrite most/all of them with
    // saved values from localStorage.
    //
    const savedSettings: Settings = loadFromLocalStorage(LOCAL_STORAGE_KEY_SETTINGS) || { } as Settings

	// Get the keys as an array of the specific types
	const validKeys = Object.keys(presets[defaultPreset].defaults) as DefaultKeys[]

	const initialValues: Settings = {
		preset: defaultPreset,
		showNetStats: true,
		showNetStatsCombos: true,
		preferenceType: PREFS_PREFER,
	}

	validKeys.forEach(key => {
		const savedValue = savedSettings[key]
		const presetDefault = presets[defaultPreset].defaults[key]
		const isValid = key === 'idealNumPositions' ? isValidIdealNumPositions(savedValue) : isValidSettingNumber(savedValue)
        initialValues[key] = isValid ? savedValue : presetDefault
    })

    // Load the player's preset if valid
	const savedPreset = savedSettings.preset
	if (savedPreset && savedPreset in presets) {
		initialValues.preset = savedPreset
		if (savedPreset !== PRESET_CUSTOM) {
			Object.entries(presets[savedPreset].defaults).forEach(([ key, value ]) => {
				initialValues[key as DefaultKeys] = value
			})
		}
	}

    // 'showNetStats' is true unless the player has deliberately turned it off
    if (savedSettings.showNetStats !== undefined && !savedSettings.showNetStats) {
        initialValues.showNetStats = false
    }

	// 'showNetStatsCombos' is true unless the player has deliberately turned it off
	if (savedSettings.showNetStatsCombos !== undefined && !savedSettings.showNetStatsCombos) {
		initialValues.showNetStatsCombos = false
	}

	// 'preferenceType'
	const savedPreferenceType = savedSettings.preferenceType
	if (savedPreferenceType && ALL_PREFS.includes(savedPreferenceType)) {
		initialValues.preferenceType = savedPreferenceType
	}

    return initialValues
}

const isValidSettingNumber = (n: unknown) => typeof n === 'number' && Number.isInteger(n) && n >= 0 && n <= 10
const isValidIdealNumPositions = (n: unknown) => typeof n === 'number' && Number.isInteger(n) && n >= 1

//
// Used by Games.jsx
//
export function scrollGamesTab() {

    const [ listElement ] = document.getElementsByClassName('list-games')

    if (listElement) {
        const presentGames = listElement.getElementsByClassName('date-present')
        const futureGames = listElement.getElementsByClassName('date-future')
        if (presentGames.length || futureGames.length) {
            const arr = listElement.getElementsByClassName('date-past')
            const el = arr[arr.length - 1]
            const target = el?.closest('.game')
            if (target) {
                target.scrollIntoView({ behavior: 'smooth' })
            }
        }
    }
}

async function enableSync(args: CallbackInterface, connector: Connector) {

	const { snapshot, set } = args

	console.log('enableSync', connector)

	console.log('connector', connector)

	const newTId = await snapshot.getPromise(newTeamId)
	if (newTId) {
		set(newSeasonConnector, connector)
	} else {
		const { team, season } = await snapshot.getPromise(section)

		if (team && season) {
			const dbid = `/teams/${team}/seasons`

			const data = {
				connector,
			}

			console.log("Saving this data", data, "for id", season)
			await updateDoc(doc(db, dbid, season), data)
			console.log("Doc updated", dbid)
		}
	}
}

export const useEnableSync = () => useRecoilCallback(args => (connector: Connector) => enableSync(args, connector))

async function disableSync(args: CallbackInterface) {

	const { snapshot, set } = args

	const newTId = await snapshot.getPromise(newTeamId)
	if (newTId) {
		console.log('set newSeasonConnector', null)
		set(newSeasonConnector, null)
	} else {
		const { team, season } = await snapshot.getPromise(section)

		if (team && season) {
			console.log('disableSync', season)

			const dbid = `/teams/${team}/seasons`

			await updateDoc(doc(db, dbid, season), {
				connector: deleteField(),
			})

			console.log("Doc updated", dbid)
		}
	}
}

export const useDisableSync = () => useRecoilCallback(args => () => disableSync(args))

async function lockGame(args: CallbackInterface, gameId: GameId) {
	const { snapshot } = args
	const { team, season } = await snapshot.getPromise(section)
	if (team && season) {
		await updateDoc(doc(db, `/teams/${team}/seasons/${season}/games`, gameId), { syncLock: true })
	}
}

async function unlockGame(args: CallbackInterface, gameId: GameId) {
	const { snapshot } = args
	const { team, season } = await snapshot.getPromise(section)
	if (team && season) {
		await updateDoc(doc(db, `/teams/${team}/seasons/${season}/games`, gameId), { syncLock: deleteField() })
	}
}

async function skipFixtureMatch(args: CallbackInterface, matchId: ConnectorMatchId) {
	const { snapshot } = args
	const { team, season } = await snapshot.getPromise(section)
	if (team && season) {
		await updateDoc(doc(db, `/teams/${team}/seasons`, season), {
			'connector.skipMatchIds': arrayUnion(matchId),
		})
	}
}


async function removeSkipMatchId(args: CallbackInterface, matchId: ConnectorMatchId) {
	const { snapshot } = args
	const { team, season } = await snapshot.getPromise(section)
	if (team && season) {
		await updateDoc(doc(db, `/teams/${team}/seasons`, season), {
			'connector.skipMatchIds': arrayRemove(matchId),
		})
	}
}

export const useLockGame = () => useRecoilCallback(args => (gameId: GameId) => lockGame(args, gameId))
export const useUnlockGame = () => useRecoilCallback(args => (gameId: GameId) => unlockGame(args, gameId))
export const useSkipFixtureMatch = () => useRecoilCallback(args => (matchId: ConnectorMatchId) => skipFixtureMatch(args, matchId))
export const useRemoveSkipMatchId = () => useRecoilCallback(args => (matchId: ConnectorMatchId) => removeSkipMatchId(args, matchId))

async function syncFixture(args: CallbackInterface, connector: Connector, numPeriods: number, mode: FixtureSyncMode): Promise<SyncResult | null> {

	console.log('syncFixture()', connector, mode)

	const { snapshot } = args

	const team = await snapshot.getPromise(myTeam)
	const games = await snapshot.getPromise(myGames)

	if (!team || !games)
		return null

	try {
		let fixtureGames: FixtureSyncGame[]
		if (connector.type === 'NetballConnect') {
			fixtureGames = await fetchNetballConnectFixtureGames(connector, games, numPeriods, mode)
		} else if (connector.type === 'PlayHQ') {
			fixtureGames = await fetchPlayHqSyncFixtureGames(connector, games, numPeriods, mode)
		} else {
			console.error("Unknown connector:", connector)
			return null
		}

		if (!fixtureGames.length) {
			console.log("No matches in fetched data; can't sync.")
			return null
		}

		return buildFixtureSyncResult(fixtureGames, games, connector.skipMatchIds)
	} catch (err) {
		console.error("Fixture sync failed", connector, err)
	}
	return null
}

type NetballConnectFixtureGame = FixtureSyncGame & {
	meScore?: number | null
	themScore?: number | null
	amTeam1?: boolean
	endTime?: string | null
	matchEnded?: boolean | null
	matchStatus?: string | null
	team1ResultId?: number | null
	team2ResultId?: number | null
}

async function fetchNetballConnectFixtureGames(
	connector: NetballConnectConnector,
	games: Games,
	numPeriods: number,
	mode: FixtureSyncMode,
): Promise<FixtureSyncGame[]> {
	const { params } = connector
	const url = `https://api-netball.squadi.com/livescores/round/matches?competitionId=${params.competitionId}&teamIds=[${params.id}]&ignoreStatuses=[1]`

	console.log('FETCH', url)

	const data = await fetchJson<NetballConnectMatches>(url)
	const matches = data.rounds.map(obj => obj.matches).flat()
	const newGames: NetballConnectFixtureGame[] = matches.map(match => ({
		id: match.id,
		date: (new Date(match.startTime)).getTime(),
		opponent: String(match.team1Id === params.id ? match.team2.name : match.team1.name).trim(),
		round: String(match.round.name).replace('Round ', ''),
		venue: `Court ${match.venueCourt.courtNumber}`,
		meScore: match.team1Id === params.id ? match.team1Score : match.team2Score,
		themScore: match.team1Id === params.id ? match.team2Score : match.team1Score,
		amTeam1: match.team1Id === params.id,
		endTime: match.endTime,
		matchEnded: match.matchEnded,
		matchStatus: match.matchStatus,
		team1ResultId: match.team1ResultId,
		team2ResultId: match.team2ResultId,
	})).filter(match => match.opponent !== 'Bye')

	const allPairings = pairFixtureGames(newGames, games)
	for (const [gameId, matchId] of allPairings) {
		const match = newGames.find(g => g.id === matchId)
		if (match) match.myGameId = gameId
	}

	const skipIds = new Set(connector.skipMatchIds ?? [])
	const lockedMatchIds = new Set([...allPairings].filter(([gid]) => games[gid].syncLock).map(([, matchId]) => matchId))
	const scoreTargets = newGames.filter(g => !skipIds.has(g.id) && !lockedMatchIds.has(g.id))

	await Promise.all(scoreTargets.map(async game => {
		await attachNetballConnectScores(game, connector, games, numPeriods, mode)
	}))

	console.log("newGames after periodScores", newGames)

	return newGames.map(game => ({
		id: game.id,
		date: game.date,
		opponent: game.opponent,
		round: game.round,
		venue: game.venue,
		...(game.result ? { result: game.result } : {}),
	}))
}

async function attachNetballConnectScores(
	game: NetballConnectFixtureGame,
	connector: NetballConnectConnector,
	games: Games,
	numPeriods: number,
	mode: FixtureSyncMode,
) {
	if (game.date > Date.now())
		return

	const localGame = game.myGameId ? games[game.myGameId] : undefined
	const localGameResult = localGame?.result
	const localGameHasDetailedScores = hasDetailedPeriodScores(localGameResult, numPeriods)

	// If NetballConnect only gives us a different final score, we could theoretically
	// replace just the final period while keeping existing detailed scores. For now,
	// leave detailed local results alone: the headline-only API data is not reliable
	// enough to partially rewrite a valid period-by-period result.
	if (
		localGameHasDetailedScores &&
		finalScoresMatch(localGameResult, game.meScore, game.themScore, numPeriods)
	) {
		game.result = formatResult(localGameResult, numPeriods)
		return
	}

	const periodScoreAttempt = {
		cacheKey: queryNetballConnectPeriodScoreCacheKey(connector, game),
		mode,
		gameDate: game.date,
		localGameHasDetailedScores: localGameHasDetailedScores,
		status: netballConnectScoreStatus(game),
		meScore: game.meScore,
		themScore: game.themScore,
	}

	if (!shouldFetchPeriodScores(periodScoreAttempt)) {
		if (canUseHeadlineFinalScore(game, localGameHasDetailedScores)) {
			game.result = makeFinalOnlyResult(game.meScore, game.themScore, numPeriods)
		}
		return
	}

	const matchId = game.id
	const competitionId = connector.params.competitionId
	const url = `https://api-netball.squadi.com/livescores/matches/periodScores?matchId=${matchId}&source=web&competitionId=${competitionId}&sportRefId=1&requestTimeStamp=${(new Date()).toISOString()}`

	try {
		const periodScoresData = await fetchJson<NetballConnectPeriodScores>(url)

		if (!periodScoresData?.length) {
			rememberPeriodScoreFetchAttempt(periodScoreAttempt, false)
			if (canUseHeadlineFinalScore(game, localGameHasDetailedScores)) {
				game.result = makeFinalOnlyResult(game.meScore, game.themScore, numPeriods)
				console.warn("No periodScoresData for game - using final score only", game)
			} else {
				console.warn("No periodScoresData for game - not syncing final score yet", game)
			}
			return
		}

		const responseMatchId = periodScoresData[0].matchId
		if (responseMatchId !== undefined && responseMatchId !== matchId) {
			throw new Error("Mismatched matchId " + matchId)
		}

		const result: Result = {
			me: new Array(numPeriods).fill(null),
			them: new Array(numPeriods).fill(null),
		}
		const officialPeriods = periodScoresData.map(obj => obj.period)
		const haveMatchingPeriodCount = periodScoresData.length === numPeriods
			&& new Set(officialPeriods).size === numPeriods
			&& officialPeriods.every(period => Number.isInteger(period) && period >= 1 && period <= numPeriods)

		if (haveMatchingPeriodCount) {
			periodScoresData.forEach(obj => {
				const periodIndex = obj.period - 1
				result.me[periodIndex] = obj[game.amTeam1 ? 'team1Score' : 'team2Score']
				result.them[periodIndex] = obj[game.amTeam1 ? 'team2Score' : 'team1Score']
			})
		} else {
			console.warn("Not syncing period-by-period scores - official fixture period count doesn't match season", {
				game,
				numPeriods,
				officialPeriods,
			})
		}

		if (game.meScore !== undefined && game.meScore !== null) {
			result.me[numPeriods - 1] = game.meScore
		}
		if (game.themScore !== undefined && game.themScore !== null) {
			result.them[numPeriods - 1] = game.themScore
		}

		const hasValidPeriodScores = haveMatchingPeriodCount && isValidResult(result, numPeriods)
		rememberPeriodScoreFetchAttempt(periodScoreAttempt, hasValidPeriodScores)

		if (hasValidPeriodScores) {
			game.result = result
		} else if (canUseHeadlineFinalScore(game, localGameHasDetailedScores)) {
			game.result = makeFinalOnlyResult(game.meScore, game.themScore, numPeriods)
			console.warn("Not syncing invalid period-by-period scores - using final score only", result, "for game", game, "with numPeriods", numPeriods)
		} else {
			console.error("Not syncing scores - this isn't a valid result", result, "for game", game, "with numPeriods", numPeriods)
		}
	} catch (err) {
		console.error("Failed to fetch", url, err)
	}
}

async function fetchPlayHqSyncFixtureGames(
	connector: PlayHqConnector,
	games: Games,
	numPeriods: number,
	mode: FixtureSyncMode,
): Promise<FixtureSyncGame[]> {
	const newGames = await fetchPlayHqFixtureGames(connector)
	const allPairings = pairFixtureGames(newGames, games)
	for (const [gameId, matchId] of allPairings) {
		const match = newGames.find(g => g.id === matchId)
		if (match) match.myGameId = gameId
	}

	const skipIds = new Set(connector.skipMatchIds ?? [])
	const lockedMatchIds = new Set([...allPairings].filter(([gid]) => games[gid].syncLock).map(([, matchId]) => matchId))
	const scoreTargets = newGames.filter(g => !skipIds.has(g.id) && !lockedMatchIds.has(g.id))

	await Promise.all(scoreTargets.map(async game => {
		await attachPlayHqScores(game, connector, games, numPeriods, mode)
	}))

	return newGames.map(game => ({
		id: game.id,
		date: game.date,
		opponent: game.opponent,
		round: game.round,
		venue: game.venue,
		...(game.result ? { result: game.result } : {}),
	}))
}

async function attachPlayHqScores(
	game: PlayHqFixtureGame,
	connector: PlayHqConnector,
	games: Games,
	numPeriods: number,
	mode: FixtureSyncMode,
) {
	if (game.date > Date.now() || !isCompletedPlayHqStatus(game.status))
		return

	const localGame = game.myGameId ? games[game.myGameId] : undefined
	const result = await fetchPlayHqResult(game, connector.params.tenant, numPeriods, mode, localGame?.result)
	if (result) game.result = result
}

function queryNetballConnectPeriodScoreCacheKey(connector: NetballConnectConnector, game: NetballConnectFixtureGame) {
	return [
		'NetballConnect',
		connector.params.competitionId,
		connector.params.id,
		game.id,
	].join(':')
}

function netballConnectScoreStatus(game: NetballConnectFixtureGame) {
	return [
		game.matchStatus ?? 'unknown',
		game.matchEnded ? 'ended' : 'not-ended',
		game.endTime ? 'has-end-time' : 'no-end-time',
		game.team1ResultId ?? 'no-team1-result',
		game.team2ResultId ?? 'no-team2-result',
	].join(':')
}

//
// Strip out 'id' field and save to firebase
//
type SaveItemData<T> = Partial<T> & { id: string }

async function saveItem<T extends Item>(args: CallbackInterface, name: keyof typeof dbids, newData: SaveItemData<T>) {
	const { snapshot } = args

	const s = await snapshot.getPromise(section)
	//if (!(name in dbids)) {
	//	throw new Error("Illegal attempt to save item type " + name)
	//}
	//const dbid = queryDbid(name as keyof typeof dbids, s)
	const dbid = queryDbid(name, s)
	const { id, ...obj } = { ...newData }
	await updateDoc(doc(db, dbid, id), obj)
	console.log("Doc updated", dbid, id)
}

// export const useSaveItem = <T extends Item>() => useRecoilCallback(args => (name: keyof typeof dbids, newData: SaveItemData<T>) => saveItem<T>(args, name, newData))

export const useSaveItem = <T extends Item>() => useRecoilCallback(args => (name: keyof { [K in keyof DbidMapping]: DbidMapping[K] extends T ? K : never }, newData: SaveItemData<T>) => saveItem<T>(args, name, newData))

function finalScoresMatch(result: Result | undefined, meScore: number | null | undefined, themScore: number | null | undefined, numPeriods: number) {
	if (meScore === null || meScore === undefined || themScore === null || themScore === undefined) {
		return false
	}

	const formatted = formatResult(result, numPeriods)
	const finalPeriod = numPeriods - 1

	return formatted.me[finalPeriod] === meScore && formatted.them[finalPeriod] === themScore
}

function fixtureGameDedupKey(game: Pick<Game, 'date' | 'opponent' | 'round' | 'venue'>) {
	return [
		String(game.date),
		String(game.round).trim().toLowerCase(),
		String(game.opponent).trim().toLowerCase(),
		String(game.venue).trim().toLowerCase(),
	].join('__')
}

export const useSyncFixture = () => useRecoilCallback(args => (connector: Connector, numPeriods: number, mode: FixtureSyncMode) => syncFixture(args, connector, numPeriods, mode))

async function importChanges(args: CallbackInterface, changes: Changes): Promise<number> {

	console.log('importChanges()', changes)

	const { snapshot } = args

	const s = await snapshot.getPromise(section)

	const dbid = `/teams/${s.team}/seasons/${s.season}/games`
	const existingGames = await getDocs(collection(db, dbid))
	const knownGameKeys = new Set<string>()

	existingGames.forEach(docSnap => {
		const data = docSnap.data()
		if (
			typeof data.date === 'number' &&
			typeof data.opponent === 'string' &&
			typeof data.round === 'string' &&
			typeof data.venue === 'string'
		) {
			knownGameKeys.add(fixtureGameDedupKey({
				date: data.date,
				opponent: data.opponent,
				round: data.round,
				venue: data.venue,
			}))
		}
	})

	const errors: string[] = []

	for (const change of changes) {
		try {

			if ('changes' in change) {
				const { myGameId } = change

				const data: Partial<Game> = { }
				change.changes.forEach(obj => {
					const { field, newValue } = obj
					if (typeof newValue === 'object' && field === 'result') {
						data[field] = newValue
					} else if (typeof newValue === 'number' && (field === 'date' || field === 'created')) {
						data[field] = newValue
					} else if (typeof newValue === 'string' && (field === 'opponent' || field === 'round' || field === 'venue')) {
						data[field] = newValue
					} else {
						console.error("Unexpected change", obj)
					}
				})

				console.log("Writing data for change", change, data, "to gameId", myGameId)

				await updateDoc(doc(db, dbid, myGameId), data)
				console.log("Doc updated", dbid, myGameId)
			} else if ('add' in change) {
				// Add new game, but skip if an equivalent game already exists.
				const rawGame = change.game as Omit<Game, 'id'> & { id?: unknown }
				const { id: _ignoredSourceId, ...gameWithoutId } = rawGame
				const data = {
					...gameWithoutId,
					created: Date.now(),
				} as Omit<Game, 'id'>

				const dedupKey = fixtureGameDedupKey(data)
				if (knownGameKeys.has(dedupKey)) {
					console.log("Skipping duplicate fixture game add", data)
					continue
				}

				const docRef = await addDoc(collection(db, dbid), data)
				knownGameKeys.add(dedupKey)
				console.log("Doc added", docRef?.id)
			} else if ('delete' in change) {
				await deleteDoc(doc(db, dbid, change.delete))
				console.log('Deleted game', change)
				console.log('Trying to delete roster', change)
				await deleteDoc(doc(db, `/teams/${s.team}/seasons/${s.season}/rosters`, change.delete))
			} else {
				console.error("Whaaaat?")
			}
		} catch (err) {
			console.error("Error importing fixture change", change, err)
			errors.push(String(err))
		}
	}

	console.log("Successfully wrote all changes.")

	if (errors.length) {
		console.error(`Import had ${errors.length} error${errors.length === 1 ? '' : 's'}.`)
	}

	return errors.length
}

export const useImportChanges = () => useRecoilCallback(args => (changes: Changes) => importChanges(args, changes))

const importPreviousSeasonPlayers = async (args: CallbackInterface, prevSeasonId: SeasonId) => {
	const { snapshot } = args

	const s = await snapshot.getPromise(section)

	if (!s.team || !s.season) {
		throw new Error("Missing team or season in section")
	}

	const prevDbid 	= `/teams/${s.team}/seasons/${prevSeasonId}/players`
	const newDbid 	= `/teams/${s.team}/seasons/${s.season}/players`

	const prevColRef = collection(db, prevDbid)

	const playerDocs = await getDocs(prevColRef)

    const players = playerDocs.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
    }))

    console.log('previousSeasonPlayers', players)

	const newColRef = collection(db, newDbid)

    const batch = writeBatch(db)

    playerDocs.forEach(oldDoc => {
        const newDocRef = doc(newColRef)
        batch.set(newDocRef, oldDoc.data())
    })

    await batch.commit()
    console.log(`Copied ${playerDocs.size} players to season ${s.season}`)
}

export const useImportPreviousSeasonPlayers = () => useRecoilCallback(args => (prevSeasonId: SeasonId) => importPreviousSeasonPlayers(args, prevSeasonId))
